import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabase } from '@lib/supabase';
import { useAuthStore } from '@store/auth';
import { track } from '@lib/posthog';
import { completeMcpHandoff } from './mcp-handoff';

/**
 * Google (OAuth/PKCE) callback handler.
 *
 * Akış: Google → Supabase → `${origin}/auth/callback?code=...`
 * Burada URL'deki `code` PKCE verifier ile session'a takas edilir.
 *
 * Race notu: Supabase client `detectSessionInUrl: true` ile init olurken
 * URL'deki `code`'u OTOMATİK exchange etmeye çalışır. Bizim manuel
 * `exchangeCodeForSession` çağrımız bununla yarışabilir (kod tek kullanımlık).
 * Bu yüzden manuel exchange hata verirse `getSession()` ile otomatik-exchange'in
 * session kurup kurmadığını kontrol ediyoruz — kurmuşsa başarı sayıyoruz.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  // StrictMode / re-render'da iki kez exchange denemesini engelle
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const errorParam = params.get('error') || params.get('error_description');

      // Google iptal/hata ile geri döndüyse code gelmez
      if (errorParam || !code) {
        navigate('/auth/login?error=oauth_failed', { replace: true });
        return;
      }

      const supabase = await getSupabase();

      const { data, error } = await supabase.auth.exchangeCodeForSession(code);

      if (!error && data.session) {
        setSession(data.session);
        track('login', { method: 'google' });
        if (await completeMcpHandoff()) return;
        navigate('/dashboard', { replace: true });
        return;
      }

      // Manuel exchange başarısız — detectSessionInUrl otomatik takas etmiş
      // olabilir; mevcut session'ı kontrol et.
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session) {
        setSession(sessionData.session);
        track('login', { method: 'google' });
        if (await completeMcpHandoff()) return;
        navigate('/dashboard', { replace: true });
        return;
      }

      navigate('/auth/login?error=oauth_failed', { replace: true });
    })();
  }, [navigate, setSession]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-muted-foreground">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent" />
      <p className="text-sm">Giriş yapılıyor…</p>
    </div>
  );
}
